import { useEffect, useState } from 'react';
import { baseAxios } from '../utils/config';

const businessHoursApi = (organizationId, locationId) =>
  `organization/${organizationId}/location/${locationId}/business-hours`;

/**
 * Loads the saved time zone and weekly hours for one salon.
 *
 * Each result is tagged with the salon it belongs to, so `loading` is derived
 * rather than reset inside the effect: after the selection changes, the hook
 * reports loading until the response for the new salon arrives, and a late
 * response for a previous salon is ignored.
 *
 * @param {number|string} organizationId - organization in the request path
 * @param {number|string} locationId - selected salon; falsy means none selected
 * @returns {{ businessHours: object|null, loading: boolean, error: Error|null }}
 *   `businessHours` is the saved row, or null when the salon was never configured.
 */
export const useLocationBusinessHours = (organizationId, locationId) => {
  const key = locationId ? String(locationId) : null;
  const [result, setResult] = useState({
    key: null,
    businessHours: null,
    error: null,
  });

  useEffect(() => {
    if (!key) return undefined;

    let cancelled = false;
    baseAxios
      .get(businessHoursApi(organizationId, key))
      .then((res) => {
        if (cancelled) return;
        setResult({
          key,
          businessHours: res.data?.businessHours ?? null,
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setResult({ key, businessHours: null, error });
      });

    return () => {
      cancelled = true;
    };
  }, [organizationId, key]);

  const isCurrent = key !== null && result.key === key;
  return {
    businessHours: isCurrent ? result.businessHours : null,
    loading: key !== null && !isCurrent,
    error: isCurrent ? result.error : null,
  };
};
